import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { RefreshCw, ArrowLeftRight, CheckCircle2, XCircle } from 'lucide-react';

const STATUSES = ['lead', 'sale', 'reject', 'upsell', 'registration', 'deposit', 'trash'];
const PAGE_SIZE = 50;

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export default function S2SLogs() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('all');
  const [status, setStatus] = useState('all');
  const [result, setResult] = useState('all'); // all | ok | failed

  useEffect(() => {
    axios
      .get(API + '/campaigns')
      .then((res) => setCampaigns(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCampaigns([]));
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (campaignId !== 'all') params.campaign_id = campaignId;
      if (status !== 'all') params.status = status;
      if (result !== 'all') params.ok = result === 'ok';
      const res = await axios.get(API + '/logs/s2s', { params });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error loading S2S log:', err);
      toast.error(t('Error loading S2S log'));
    } finally {
      setLoading(false);
    }
  }, [page, campaignId, status, result, t]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [campaignId, status, result]);

  const campaignName = (id) => {
    const c = campaigns.find((x) => x.id === id);
    return c ? c.name : id;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Admin')}
        title={t('S2S postback log')}
        subtitle={t('Every outbound conversion postback fired across your campaigns — filter by campaign, status or result.')}
        actions={
          <Button variant="outline" onClick={fetchLog} disabled={loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        }
      />

      <div className="flex flex-wrap gap-3">
        <div className="w-64">
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger><SelectValue placeholder={t('All campaigns')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All campaigns')}</SelectItem>
              {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue placeholder={t('Any status')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('Any status')}</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={result} onValueChange={setResult}>
            <SelectTrigger><SelectValue placeholder={t('Any result')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('Any result')}</SelectItem>
              <SelectItem value="ok">{t('Success')}</SelectItem>
              <SelectItem value="failed">{t('Failed')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="skeleton h-96 w-full"></div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty
              icon={ArrowLeftRight}
              title={t('No postbacks yet')}
              subtitle={t('Outbound conversion postbacks will appear here once your campaigns start forwarding conversions.')}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Time')}</TableHead>
                  <TableHead>{t('Campaign')}</TableHead>
                  <TableHead>{t('Status')}</TableHead>
                  <TableHead>{t('Result')}</TableHead>
                  <TableHead>{t('HTTP')}</TableHead>
                  <TableHead>{t('URL')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtTime(e.created_at)}</TableCell>
                    <TableCell className="font-medium">{campaignName(e.campaign_id)}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{e.status}</Badge></TableCell>
                    <TableCell>
                      {e.ok ? (
                        <span className="inline-flex items-center text-emerald-600 text-sm"><CheckCircle2 className="w-4 h-4 mr-1" />{t('Success')}</span>
                      ) : (
                        <span className="inline-flex items-center text-rose-600 text-sm" title={e.error || ''}><XCircle className="w-4 h-4 mr-1" />{t('Failed')}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{e.http_status || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[28rem] truncate" title={e.url || ''}>{e.url || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('Page')} {page} / {totalPages} · {total} {t('entries')}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('Previous')}</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t('Next')}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
